import React from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useDispatch, useSelector } from "react-redux";
import { setOrderScheduling } from "../ducks/checkoutSlice";
import useWorkingTime from "../hooks/useWorkingTime";
import SchedulerDay from "./SchedulerDay";

export default function OrderSchedulerModal({ isOpen, onClose }) {
  const dispatch = useDispatch();

  const shop = useSelector((state) => state.shop);
  const { toGo, toDelivery } = useSelector((state) => state.checkout);
  const { daysAheadToSchedule, workingScheduleToGo, workingScheduleDelivery } =
    shop;
  const { isWorkingDelivery, isWorkingToGo } = useWorkingTime(shop);

  const workingSchedule = toGo
    ? workingScheduleToGo
    : toDelivery
      ? workingScheduleDelivery
      : {};

  const isStoreWorking = toGo
    ? isWorkingToGo
    : toDelivery
      ? isWorkingDelivery
      : false;

  const orderScheduling = useSelector(
    (state) => state.checkout.orderScheduling,
  );

  const availableDaysForScheduling = getAvailableDaysForScheduling(
    daysAheadToSchedule,
    workingSchedule,
  );

  const initialSelectedDay = orderScheduling.isScheduledOrder
    ? orderScheduling.schedule.date
    : availableDaysForScheduling[0];

  const initialSelectedInterval = orderScheduling.isScheduledOrder
    ? orderScheduling.schedule.hoursInterval
    : "";
  const [selectedInterval, setSelectedInterval] = React.useState(
    initialSelectedInterval,
  );
  const [selectedDay, setSelectedDay] = React.useState(initialSelectedDay);

  const availableHourIntervalsForScheduling = getAvailableHoursForScheduling(
    selectedDay,
    workingSchedule,
  );

  const selectDay = (day) => () => setSelectedDay(day);
  const handleChangeSelectedInterval = (event) =>
    setSelectedInterval(event.target.value);

  const setScheduling = () => {
    const scheduledDate = new Date(selectedDay);
    dispatch(
      setOrderScheduling({
        isScheduledOrder: !!selectedInterval,
        schedule: selectedInterval
          ? {
              date: scheduledDate.toISOString("pt-BR", { dateStyle: "short" }),
              hoursInterval: selectedInterval,
            }
          : null,
      }),
    );
    onClose();
  };

  const today = new Date();
  const selectedDate = new Date(selectedDay);
  const isTodaySelected = today.getDate() === selectedDate.getDate();
  const getBorderColorForSelectedItem = (baseValue, selectedValue) =>
    baseValue === selectedValue ? "btn-shop" : "bg-white border-secondary";
  const getTextColorForSelectedItem = (baseDate) =>
    baseDate === selectedDay ? "btn-shop" : "text-secondary";
  const confirmEnable =
    (selectedDay && selectedInterval) ||
    (isTodaySelected && isStoreWorking && selectedInterval === "");

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      dialogClassName="h-100"
      contentClassName="h-100"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title as="h3">Quando quer receber?</Modal.Title>
      </Modal.Header>
      <div className="d-flex flex-wrap m-3">
        {availableDaysForScheduling
          .map((day) => new Date(day))
          .map((date) => (
            <SchedulerDay
              key={date.toISOString()}
              date={date}
              classNames={`me-2 mb-2 ${getBorderColorForSelectedItem(
                date.toISOString(),
                selectedDay,
              )}`}
              textColor={getTextColorForSelectedItem(date.toISOString())}
              onClick={selectDay(date.toISOString())}
            />
          ))}
      </div>
      <p className="ms-3 mt-3">Horário:</p>
      <Modal.Body className="pt-0">
        <div className="d-flex flex-column">
          <div role="radiogroup" aria-label="Horário">
            {isTodaySelected && isStoreWorking && (
              <IntervalOption
                className={getBorderColorForSelectedItem("", selectedInterval)}
                value=""
                label="Agora mesmo"
                checked={selectedInterval === ""}
                onChange={handleChangeSelectedInterval}
              />
            )}
            {availableHourIntervalsForScheduling
              .map((interval) => ({
                startDate: new Date(interval.start),
                endDate: new Date(interval.end),
              }))
              .map(({ startDate, endDate }) => ({
                startDate,
                value: `${startDate.toLocaleString("pt-BR", {
                  timeStyle: "short",
                })} - ${endDate.toLocaleString("pt-BR", {
                  timeStyle: "short",
                })}`,
              }))
              .map(({ startDate, value }) => (
                <IntervalOption
                  className={`my-2 ${getBorderColorForSelectedItem(
                    value,
                    selectedInterval,
                  )}`}
                  key={startDate}
                  value={value}
                  label={value}
                  checked={selectedInterval === value}
                  onChange={handleChangeSelectedInterval}
                />
              ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="shop"
          onClick={setScheduling}
          disabled={!confirmEnable}
        >
          Confirmar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function IntervalOption({ className, value, label, checked, onChange }) {
  return (
    <label className={`radio-option border rounded ${className}`}>
      <span>{label}</span>
      <input
        type="radio"
        name="scheduling-interval"
        value={value}
        checked={checked}
        onChange={onChange}
      />
    </label>
  );
}

function getAvailableDaysForScheduling(daysAheadToSchedule, workingSchedule) {
  const oneDayStep = 86400000;
  const weekdays = Object.keys(workingSchedule);
  const today = new Date();
  today.setHours(0);
  today.setMinutes(0);
  today.setSeconds(0);
  today.setMilliseconds(0);
  const todayHours = getAvailableHoursForScheduling(
    today.toISOString(),
    workingSchedule,
  );
  const availableDaysForScheduling =
    todayHours.length > 0 ? [today.toISOString()] : [];
  const daysAheadFromToday = Array(daysAheadToSchedule * 2)
    .fill(1)
    .map((item, index) => {
      const date = new Date((item + index) * oneDayStep + today.getTime());
      return date.toISOString();
    });
  return availableDaysForScheduling
    .concat(daysAheadFromToday)
    .filter((day) => {
      const date = new Date(day);
      return weekdays.includes(`${date.getDay()}`);
    })
    .slice(0, daysAheadToSchedule);
}

function getAvailableHoursForScheduling(dayForScheduling, workingSchedule) {
  const schedulingDate = new Date(dayForScheduling);
  const workingScheduleItems = workingSchedule[schedulingDate.getDay()];
  const now = Date.now();

  if (!workingScheduleItems) return [];

  return workingScheduleItems.reduce((schedulingIntervals, item) => {
    const startDate = new Date(
      schedulingDate.getFullYear(),
      schedulingDate.getMonth(),
      schedulingDate.getDate(),
      item.start.hours,
      item.start.minutes,
      0,
    );
    const endDate = new Date(
      schedulingDate.getFullYear(),
      schedulingDate.getMonth(),
      schedulingDate.getDate(),
      item.end.hours,
      item.end.minutes,
      0,
    );
    const step = 1000 * 60 * 30;
    const hourRange = Math.ceil((endDate - startDate) / step);

    const intervals = Array(hourRange)
      .fill(startDate.getTime())
      .reduce((intervals, startTimestamp, index) => {
        const startStep = index * step;
        const endStep = step + index * step;
        const start = startTimestamp + startStep;
        const end = startTimestamp + endStep;

        if (start < now) {
          return intervals;
        }

        return intervals.concat({
          start,
          end,
        });
      }, []);

    return schedulingIntervals.concat(intervals);
  }, []);
}
